import { AxiosError } from 'axios';

import type { ApiErrorEnvelope } from '@/types/api';

interface ApiErrorOptions {
  traceId?: string;
  detail?: string;
  fieldErrors?: Readonly<Record<string, readonly string[]>>;
  /** Seconds parsed from the `Retry-After` response header (only present on 423 lockouts). */
  retryAfter?: number;
}

/** Normalized error consumers can render without unwrapping AxiosError. */
export class ApiError extends Error {
  public readonly status: number;
  public readonly traceId: string | undefined;
  public readonly detail: string | undefined;
  public readonly fieldErrors: Readonly<Record<string, readonly string[]>> | undefined;
  public readonly retryAfter: number | undefined;

  constructor(message: string, status: number, opts: ApiErrorOptions = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.traceId = opts.traceId;
    this.detail = opts.detail;
    this.fieldErrors = opts.fieldErrors;
    this.retryAfter = opts.retryAfter;
  }
}

/** Convert an AxiosError into a typed ApiError. Always defined; falls back to network-error shape. */
export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  if (error instanceof AxiosError) {
    const status = error.response?.status ?? 0;
    const data = error.response?.data as ApiErrorEnvelope | undefined;
    const retryAfter = parseRetryAfter(error);
    if (data !== undefined && typeof data === 'object' && 'title' in data) {
      const opts: ApiErrorOptions = { traceId: data.traceId };
      if (data.detail !== undefined) opts.detail = data.detail;
      if (data.errors !== undefined) opts.fieldErrors = data.errors;
      if (retryAfter !== undefined) opts.retryAfter = retryAfter;
      return new ApiError(data.title, status, opts);
    }
    if (status === 0) {
      return new ApiError('Network error — please check your connection.', 0);
    }
    return new ApiError(error.message, status, retryAfter !== undefined ? { retryAfter } : {});
  }
  if (error instanceof Error) {
    return new ApiError(error.message, 0);
  }
  return new ApiError('Unknown error', 0);
}

/**
 * Blob-aware variant of {@link toApiError} for requests using `responseType: 'blob'`.
 *
 * On those requests axios hands back the error body as a `Blob`, so the `'title' in data`
 * probe in {@link toApiError} fails and the real server message is discarded — a 403 renders
 * as the useless "Request failed with status code 403". Re-read the blob as text, and if it
 * parses as a problem+json envelope, hand the parsed object to {@link toApiError} so callers
 * get the actual `title` / `detail` / `traceId`.
 *
 * Falls back to {@link toApiError} unchanged when the body is absent, unreadable, or not JSON
 * (e.g. an HTML error page from a proxy).
 */
export async function toApiErrorFromBlob(error: unknown): Promise<ApiError> {
  if (error instanceof AxiosError && error.response?.data instanceof Blob) {
    try {
      const parsed: unknown = JSON.parse(await readBlobText(error.response.data));
      if (parsed !== null && typeof parsed === 'object' && 'title' in parsed) {
        error.response.data = parsed;
      }
    } catch {
      // Not JSON — leave the original body alone and take the generic path below.
    }
  }
  return toApiError(error);
}

/**
 * Read a Blob as text, preferring `Blob.text()` and falling back to `FileReader`.
 *
 * `Blob.text()` is absent in jsdom and in Safari before 14, and calling it there throws a
 * TypeError that would otherwise be swallowed as "not JSON" — silently costing us the error
 * message in exactly the case this helper exists to rescue.
 */
async function readBlobText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') {
    return blob.text();
  }
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(typeof reader.result === 'string' ? reader.result : '');
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error('Failed to read error response body'));
    };
    reader.readAsText(blob);
  });
}

/**
 * Parse the `Retry-After` response header into a number of seconds.
 * The backend (Phase 6 Plan 06-02 GlobalExceptionMiddleware) emits seconds (relative)
 * — never an HTTP-date — so we only accept the integer-seconds form here.
 */
function parseRetryAfter(error: AxiosError): number | undefined {
  const headers = error.response?.headers;
  if (headers === undefined) return undefined;
  // axios returns an AxiosHeaders-or-plain-object shape — normalise via bracket-access.
  const raw =
    (headers as Record<string, unknown>)['retry-after'] ??
    (headers as Record<string, unknown>)['Retry-After'];
  if (typeof raw !== 'string' && typeof raw !== 'number') return undefined;
  const n = typeof raw === 'number' ? raw : Number.parseInt(raw, 10);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}
